import { css } from "@linaria/core"
import { Decoration } from "prosemirror-view"

import { AGENT_MENTION_TRIGGER, FILE_MENTION_TRIGGER } from "@/components/Markdown/mentions.ts"

import type { TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"
import type { LinksKeyState } from "prosemirror-link-plugin"


interface MentionSpec {
  id: number
  alias: string
}

export const mentionDecoration = (
  start: number,
  end: number,
  alias: string,
  _matchPos: number,
  pluginState: LinksKeyState<MentionSpec>,
  profile: TD_MemberProfile | null
) => {
  const spec = pluginState.aliasToSpec[alias]

  if (!spec) {
    return Decoration.inline(start, end, {})
  }

  let className = UserMention

  if (alias.length > 1 && alias.substring(1) == profile?.username) {
    className = UserMeMention
  } else if (alias.startsWith(FILE_MENTION_TRIGGER)) {
    className = FileMention
  } else if (alias === AGENT_MENTION_TRIGGER) {
    className = Mention
  }

  return Decoration.inline(
    start,
    end,
    {
      class: className
    },
    { id: spec.id, alias: spec.alias }
  )
}

const UserMention = css`
  color: var(--badge-teal);
`

const UserMeMention = css`
  color: var(--warning-color);
`

const FileMention = css`
  color: var(--secondary-text-color);
`

const Mention = css`
  font-weight: 600;
`

// Extract mentions from text (@ followed by non-whitespace characters)
export const mentionPattern = /@\S+/g
export const extractMentions = (text: string, cursorPos?: number): MentionSpec[] => {
  const mentions: MentionSpec[] = []
  let match
  let idx = 0

  while ((match = mentionPattern.exec(text)) !== null) {
    const mention = match[0]
    const mentionStart = match.index
    const mentionEnd = mentionStart + mention.length

    if (cursorPos !== undefined && cursorPos >= mentionStart && cursorPos <= mentionEnd) {
      idx++
      continue
    }

    const nextChar = text[mentionEnd]
    const hasWhitespaceAfter = nextChar !== undefined && /\s/.test(nextChar)

    const isUnambiguous = mention.startsWith(FILE_MENTION_TRIGGER) || mention === AGENT_MENTION_TRIGGER

    if (hasWhitespaceAfter || isUnambiguous) {
      mentions.push({ id: idx++, alias: mention })
    } else {
      idx++
    }
  }

  return mentions
}